/*
 * The rules behind the Lightbox that need no browser: which image an arrow key or a swipe steps to,
 * how a drag across the screen is read, and what the counter says.
 */

/** The image the arrow keys step to from `index`, `by` one either way, going round at the ends. */
export const steppedImage = (index: number, count: number, by: 1 | -1) =>
  count === 0 ? 0 : (index + by + count) % count

/** The counter over the images, "2 of 5", or none for an image alone. */
export const positionOf = (index: number, count: number) =>
  count > 1 ? `${index + 1} of ${count}` : undefined

/** How far a finger moves, in px, before its drag is read as going across or down. */
const slop = 10

/** Which way a drag goes: across, stepping between the images, or down, closing them. */
export type Axis = "x" | "y"

/**
 * The way a drag goes once it has gone far enough to tell, by whichever way it went further, and
 * then kept for the rest of it; none while it is still within a finger's slop.
 */
export function axisOf(dx: number, dy: number): Axis | undefined {
  if (Math.hypot(dx, dy) < slop) return undefined
  return Math.abs(dx) > Math.abs(dy) ? "x" : "y"
}

/** What a swipe does as the finger lifts: steps to the next or previous image, closes, or nothing. */
export type Swipe = "next" | "previous" | "close" | "stay"

/** A flick counts however short, once it has gone this far, in px, and this fast, in px per ms. */
const flick = { distance: 24, speed: 0.5 }

/** A swipe across steps once it has gone this share of the screen's width. */
const stepShare = 0.25

/** A swipe down closes once it has gone this far, in px, or this share of the screen, if less. */
const close = { distance: 120, share: 0.2 }

/**
 * What a drag does as the finger lifts, by its axis, how far it went across (`dx`, leftward is
 * negative) and down (`dy`), over how long (`ms`), on a screen `width` by `height`. Across, a
 * quarter of the width or a flick steps: leftward to the next image, as a page turns. Down, 120px
 * (or a fifth of a short screen) or a flick closes. Anything short of those, or up, stays.
 */
export function swipeOf({
  axis,
  dx,
  dy,
  ms,
  width,
  height,
}: {
  axis: Axis
  dx: number
  dy: number
  ms: number
  width: number
  height: number
}): Swipe {
  const flicked = (distance: number) =>
    distance >= flick.distance && distance / Math.max(ms, 1) >= flick.speed
  if (axis === "x") {
    const across = Math.abs(dx)
    if (across < width * stepShare && !flicked(across)) return "stay"
    return dx < 0 ? "next" : "previous"
  }
  if (dy <= 0) return "stay"
  return dy >= Math.min(close.distance, height * close.share) || flicked(dy) ? "close" : "stay"
}

/** The image a swipe leaves shown: the next or the previous one, never round past either end. */
export function swipedTo(index: number, count: number, swipe: Swipe) {
  if (swipe === "next") return Math.min(index + 1, count - 1)
  if (swipe === "previous") return Math.max(index - 1, 0)
  return index
}

/**
 * How far the images follow a finger: the whole way where there is an image to step to, and a
 * third as far past the first or the last, or up, where there is nothing there, so the image gives
 * a little and comes back.
 */
export function followOf({
  axis,
  dx,
  dy,
  index,
  count,
}: {
  axis: Axis
  dx: number
  dy: number
  index: number
  count: number
}) {
  if (axis === "y") return { x: 0, y: dy > 0 ? dy : dy / 3 }
  const pastEnd = (dx > 0 && index === 0) || (dx < 0 && index >= count - 1)
  return { x: pastEnd ? dx / 3 : dx, y: 0 }
}
